import { useState, FormEvent } from 'react';

const QuoteForm = () => {
  const [formData, setFormData] = useState({
    fullName: '',
    phone: '',
    email: '',
    jobAddress: '',
    projectType: '',
    numberOfBlocks: '',
    wallHeight: '',
    additionalNotes: ''
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const projectTypes = [
    'Foundation Walls',
    'Retaining Walls',
    'Commercial Building',
    'Residential Building',
    'Basement Walls',
    'Structural Walls',
    'Other'
  ];

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const form = e.target as HTMLFormElement;
      const formDataToSend = new URLSearchParams();
      
      formDataToSend.append('fullName', formData.fullName);
      formDataToSend.append('phone', formData.phone);
      formDataToSend.append('email', formData.email);
      formDataToSend.append('jobAddress', formData.jobAddress);
      formDataToSend.append('projectType', formData.projectType);
      formDataToSend.append('numberOfBlocks', formData.numberOfBlocks);
      formDataToSend.append('wallHeight', formData.wallHeight);
      formDataToSend.append('additionalNotes', formData.additionalNotes);

      const response = await fetch('https://readdy.ai/api/form/d6hcds4bcbcsu13rg5a0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formDataToSend.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          fullName: '',
          phone: '',
          email: '',
          jobAddress: '',
          projectType: '',
          numberOfBlocks: '',
          wallHeight: '',
          additionalNotes: ''
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section className="py-20 bg-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-12">
          <h2 className="text-4xl font-bold text-slate-900 mb-4">
            Request a Block Filling Quote
          </h2>
          <p className="text-lg text-slate-600">
            Fill out the form below and we'll get back to you with a detailed quote for your project.
          </p>
        </div>

        <form 
          id="block-filling-quote-form"
          data-readdy-form
          onSubmit={handleSubmit} 
          className="bg-slate-50 rounded-lg p-8 md:p-12"
        >
          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="fullName" className="block text-sm font-semibold text-slate-900 mb-2">
                Full Name *
              </label>
              <input
                type="text"
                id="fullName"
                name="fullName"
                value={formData.fullName}
                onChange={(e) => setFormData({ ...formData, fullName: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
              />
            </div>

            <div>
              <label htmlFor="phone" className="block text-sm font-semibold text-slate-900 mb-2">
                Phone Number *
              </label>
              <input
                type="tel"
                id="phone"
                name="phone"
                value={formData.phone}
                onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="email" className="block text-sm font-semibold text-slate-900 mb-2">
              Email Address *
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              required
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
            />
          </div>

          <div className="mb-6">
            <label htmlFor="jobAddress" className="block text-sm font-semibold text-slate-900 mb-2">
              Full Job Address *
            </label>
            <input
              type="text"
              id="jobAddress"
              name="jobAddress"
              value={formData.jobAddress}
              onChange={(e) => setFormData({ ...formData, jobAddress: e.target.value })}
              required
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-6">
            <div>
              <label htmlFor="projectType" className="block text-sm font-semibold text-slate-900 mb-2">
                Type of Project *
              </label>
              <select
                id="projectType"
                name="projectType"
                value={formData.projectType}
                onChange={(e) => setFormData({ ...formData, projectType: e.target.value })}
                required
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm cursor-pointer"
              >
                <option value="">Select project type</option>
                {projectTypes.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label htmlFor="numberOfBlocks" className="block text-sm font-semibold text-slate-900 mb-2">
                Estimated Number of Blocks
              </label>
              <input
                type="text"
                id="numberOfBlocks"
                name="numberOfBlocks"
                value={formData.numberOfBlocks}
                onChange={(e) => setFormData({ ...formData, numberOfBlocks: e.target.value })}
                placeholder="e.g., 500"
                className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
              />
            </div>
          </div>

          <div className="mb-6">
            <label htmlFor="wallHeight" className="block text-sm font-semibold text-slate-900 mb-2">
              Wall Height (feet)
            </label>
            <input
              type="text"
              id="wallHeight"
              name="wallHeight"
              value={formData.wallHeight}
              onChange={(e) => setFormData({ ...formData, wallHeight: e.target.value })}
              placeholder="e.g., 8 feet"
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm"
            />
          </div>

          <div className="mb-6">
            <label htmlFor="additionalNotes" className="block text-sm font-semibold text-slate-900 mb-2">
              Additional Notes
            </label>
            <textarea
              id="additionalNotes"
              name="additionalNotes"
              value={formData.additionalNotes}
              onChange={(e) => {
                if (e.target.value.length <= 500) {
                  setFormData({ ...formData, additionalNotes: e.target.value });
                }
              }}
              rows={4}
              maxLength={500}
              placeholder="Any additional details about your project..."
              className="w-full px-4 py-3 rounded-lg border border-slate-300 focus:ring-2 focus:ring-amber-500 focus:border-transparent text-sm resize-none"
            ></textarea>
            <div className="text-right text-xs text-slate-500 mt-1">
              {formData.additionalNotes.length}/500 characters
            </div>
          </div>

          {submitStatus === 'success' && (
            <div className="mb-6 p-4 bg-green-50 border border-green-200 rounded-lg">
              <p className="text-green-800 text-sm font-medium">
                <i className="ri-checkbox-circle-line mr-2"></i>
                Thank you! Your quote request has been submitted. We'll contact you shortly.
              </p>
            </div>
          )}

          {submitStatus === 'error' && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-800 text-sm font-medium">
                <i className="ri-error-warning-line mr-2"></i>
                Something went wrong. Please try again or call us at (828) 262-0091.
              </p>
            </div>
          )}

          <button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-slate-900 text-white px-8 py-4 rounded-lg font-semibold hover:bg-slate-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
          >
            {isSubmitting ? (
              <span className="flex items-center justify-center gap-2">
                <i className="ri-loader-4-line animate-spin"></i>
                Submitting...
              </span>
            ) : (
              <span className="flex items-center justify-center gap-2">
                Submit Quote Request
                <i className="ri-send-plane-line"></i>
              </span>
            )}
          </button>
        </form>
      </div>
    </section>
  );
};

export default QuoteForm;